import React, { useEffect, useState } from 'react';
import { festivalApi } from '../../services/api';
import type { FestivalEdition, FestivalCandidate } from '../../types';
import { FestivalCard } from '../common/FestivalCard';
import { useLang } from '../../i18n/context';

type View = 'editions' | 'candidates';

export function FestivalsTab() {
  const { t } = useLang();
  const [view, setView] = useState<View>('editions');
  const [editions, setEditions] = useState<FestivalEdition[]>([]);
  const [candidates, setCandidates] = useState<FestivalCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [harvesting, setHarvesting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [eds, cands] = await Promise.all([
        festivalApi.getEditions({ horizon_days: 365 }),
        festivalApi.getCandidates().catch(() => []),
      ]);
      setEditions(eds);
      setCandidates(cands);
    } catch (err) {
      console.error('Failed to load festivals:', err);
    } finally {
      setLoading(false);
    }
  }

  async function openEdition(id: string) {
    if (expandedId === id) { setExpandedId(null); return; }
    try {
      const full = await festivalApi.getEdition(id);
      setEditions(prev => prev.map(e => e.id === id ? { ...e, lineup: full.lineup } : e));
      setExpandedId(id);
    } catch (err) {
      console.error('Failed to load edition:', err);
    }
  }

  async function runHarvest() {
    setHarvesting(true);
    try {
      const r = await festivalApi.harvestCandidates();
      window.alert(`Harvest done:\n• scanned ${r.scanned_events} events\n• URL leads ${r.url_leads}, caption leads ${r.caption_leads}\n• candidates: +${r.candidates_created} new, ${r.candidates_updated} updated, ${r.auto_queued_tier1} tier-1 auto-queued`);
      await loadAll();
    } catch (err: any) {
      window.alert('Harvest failed: ' + err.message);
    } finally {
      setHarvesting(false);
    }
  }

  async function dismissCandidate(id: string) {
    try {
      await festivalApi.updateCandidate(id, { status: 'dismissed' });
      setCandidates(prev => prev.filter(c => c.id !== id));
    } catch (err) { console.error(err); }
  }

  async function rejectCandidate(id: string) {
    try {
      await festivalApi.updateCandidate(id, { status: 'rejected' });
      setCandidates(prev => prev.filter(c => c.id !== id));
    } catch (err) { console.error(err); }
  }

  const pillStyle = (active: boolean): React.CSSProperties => ({
    padding: '5px 12px', fontSize: 12, fontWeight: 700, letterSpacing: '0.5px',
    borderRadius: 4, border: '1px solid var(--border-color)',
    background: active ? 'rgba(201,168,76,0.15)' : 'transparent',
    color: active ? 'var(--gold)' : 'var(--text-muted)',
    cursor: 'pointer', textTransform: 'uppercase',
  });

  if (loading) return <div className="loading"><span className="spinner"></span> {t('common.loading')}</div>;

  // Group editions by proximity: in-window (<60d) vs upcoming (>60d)
  const now = Date.now();
  const MS_60D = 60 * 86400000;
  const inWindow = editions.filter(e => new Date(e.start_date).getTime() - now <= MS_60D && new Date(e.end_date).getTime() >= now);
  const upcoming = editions.filter(e => new Date(e.start_date).getTime() - now > MS_60D);
  const live = editions.filter(e => new Date(e.start_date).getTime() <= now && new Date(e.end_date).getTime() >= now);

  return (
    <div>
      <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 6, fontSize: 12 }}>
        <strong style={{ color: 'var(--gold)' }}>⚡ ADMIN · Phase F (work in progress)</strong>
        <span style={{ color: 'var(--text-muted)', marginLeft: 10 }}>
          Seed data with approximate dates — verify each edition before publishing. Agenda visibility: ≤ 60 days before start.
        </span>
      </div>

      {/* View toggle */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' }}>
        <button style={pillStyle(view === 'editions')} onClick={() => setView('editions')}>
          Editions ({editions.length})
        </button>
        <button style={pillStyle(view === 'candidates')} onClick={() => setView('candidates')}>
          Candidates ({candidates.length})
        </button>

        {view === 'candidates' && (
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={runHarvest} disabled={harvesting}>
            {harvesting ? '⏳ harvesting…' : '🪤 Run cross-handle harvest'}
          </button>
        )}
      </div>

      {view === 'editions' && (
        <>
          {live.length > 0 && (
            <>
              <div className="section-header" style={{ marginBottom: 8 }}>
                <span className="section-title" style={{ color: 'var(--red)' }}>🔴 LIVE NOW</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{live.length}</span>
              </div>
              {live.map(f => (
                <div key={f.id} style={{ marginBottom: expandedId === f.id ? 0 : 6 }}>
                  <FestivalCard
                    festival={f}
                    variant={expandedId === f.id ? 'mega' : 'compact'}
                    onClick={() => openEdition(f.id)}
                  />
                  {expandedId === f.id && <EditionDetail edition={f} onValidated={(r) => {
                    setEditions(prev => prev.map(e => e.id === f.id ? { ...e,
                      validation_verdict: r.verdict,
                      genre_keyword_density: r.density_per_1000,
                      has_dub_stage_detected: r.detected_stages.length > 0 ? 1 : 0,
                      detected_genre_tags: JSON.stringify(r.detected_genre_tags),
                      validated_at: new Date().toISOString(),
                      validation_log: r.notes,
                    } : e));
                  }} />}
                </div>
              ))}
            </>
          )}

          {inWindow.length > 0 && (
            <>
              <div className="section-header" style={{ marginTop: live.length ? 20 : 0, marginBottom: 8 }}>
                <span className="section-title">📅 IN THE NEXT 2 MONTHS</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{inWindow.length}</span>
              </div>
              {inWindow.filter(e => !live.includes(e)).map(f => (
                <div key={f.id} style={{ marginBottom: expandedId === f.id ? 0 : 6 }}>
                  <FestivalCard
                    festival={f}
                    variant={expandedId === f.id ? 'mega' : 'compact'}
                    onClick={() => openEdition(f.id)}
                  />
                  {expandedId === f.id && <EditionDetail edition={f} onValidated={(r) => {
                    setEditions(prev => prev.map(e => e.id === f.id ? { ...e,
                      validation_verdict: r.verdict,
                      genre_keyword_density: r.density_per_1000,
                      has_dub_stage_detected: r.detected_stages.length > 0 ? 1 : 0,
                      detected_genre_tags: JSON.stringify(r.detected_genre_tags),
                      validated_at: new Date().toISOString(),
                      validation_log: r.notes,
                    } : e));
                  }} />}
                </div>
              ))}
            </>
          )}

          {upcoming.length > 0 && (
            <>
              <div className="section-header" style={{ marginTop: 20, marginBottom: 8 }}>
                <span className="section-title">🗓 LATER THIS YEAR</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{upcoming.length}</span>
              </div>
              {upcoming.map(f => (
                <div key={f.id} style={{ marginBottom: expandedId === f.id ? 0 : 6 }}>
                  <FestivalCard
                    festival={f}
                    variant={expandedId === f.id ? 'mega' : 'compact'}
                    onClick={() => openEdition(f.id)}
                  />
                  {expandedId === f.id && <EditionDetail edition={f} onValidated={(r) => {
                    setEditions(prev => prev.map(e => e.id === f.id ? { ...e,
                      validation_verdict: r.verdict,
                      genre_keyword_density: r.density_per_1000,
                      has_dub_stage_detected: r.detected_stages.length > 0 ? 1 : 0,
                      detected_genre_tags: JSON.stringify(r.detected_genre_tags),
                      validated_at: new Date().toISOString(),
                      validation_log: r.notes,
                    } : e));
                  }} />}
                </div>
              ))}
            </>
          )}

          {editions.length === 0 && (
            <div className="empty-state"><p>No festival editions seeded.</p></div>
          )}
        </>
      )}

      {view === 'candidates' && (
        <CandidatesPanel
          candidates={candidates}
          onDismiss={dismissCandidate}
          onReject={rejectCandidate}
        />
      )}
    </div>
  );
}

function EditionDetail({ edition, onValidated }: { edition: FestivalEdition; onValidated: (result: any) => void }) {
  const lineup = edition.lineup || [];
  const [validating, setValidating] = React.useState(false);

  const detectedTags: string[] = (() => {
    try { return JSON.parse(edition.detected_genre_tags || '[]'); } catch { return []; }
  })();

  async function runValidate() {
    setValidating(true);
    try {
      const { festivalApi } = await import('../../services/api');
      const r = await festivalApi.validateEdition(edition.id);
      onValidated(r);
      const lines = [
        `verdict: ${r.verdict}`,
        `density: ${r.density_per_1000} / 1000 chars (${r.total_genre_hits} hits)`,
        r.detected_stages.length ? `stages: ${r.detected_stages.join(', ')}` : 'no named stage',
        r.detected_genre_tags.length ? `genres: ${r.detected_genre_tags.join(', ')}` : 'no strong genre tag',
        r.website_url ? `source: ${r.website_url}` : 'no website',
      ].join('\n');
      window.alert(`Validator: ${edition.name}\n\n${lines}`);
    } catch (err: any) {
      window.alert('Validate failed: ' + err.message);
    } finally {
      setValidating(false);
    }
  }

  const verdictStyle: Record<string, React.CSSProperties> = {
    pure_reggae: { background: 'rgba(201,168,76,0.2)', color: 'var(--gold)' },
    mixed:       { background: 'rgba(76,175,80,0.2)',  color: 'var(--green)' },
    occasional:  { background: 'rgba(150,150,150,0.2)', color: 'var(--text-muted)' },
    no_reggae:   { background: 'rgba(229,57,53,0.2)',  color: 'var(--red)' },
    unreachable: { background: 'rgba(255,152,0,0.2)',  color: 'var(--orange)' },
  };

  return (
    <div style={{ padding: '14px 18px', background: 'rgba(0,0,0,0.2)', borderRadius: 6, marginTop: -10, marginBottom: 14 }}>
      {/* Validation row */}
      <div style={{ marginBottom: 14, padding: 10, borderRadius: 4, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', fontWeight: 700 }}>
            Site validation
          </div>
          {edition.validation_verdict && (
            <span style={{ ...verdictStyle[edition.validation_verdict], padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
              {edition.validation_verdict}
            </span>
          )}
          {edition.has_dub_stage_detected ? (
            <span style={{ background: 'rgba(76,175,80,0.15)', color: 'var(--green)', padding: '2px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700 }}>
              🎤 DUB STAGE DETECTED
            </span>
          ) : null}
          {typeof edition.genre_keyword_density === 'number' && (
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              density: <strong>{edition.genre_keyword_density}</strong> / 1000
            </span>
          )}
          {edition.validated_at && (
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              last run: {new Date(edition.validated_at).toLocaleString()}
            </span>
          )}
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }}
                  onClick={runValidate} disabled={validating || !edition.lineup_url}>
            {validating ? '⏳ fetching...' : '🔍 Validate via site'}
          </button>
        </div>
        {detectedTags.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {detectedTags.map(t => (
              <span key={t} style={{ fontSize: 11, padding: '2px 8px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 3 }}>
                {t}
              </span>
            ))}
          </div>
        )}
        {edition.validation_log && (
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6, fontFamily: 'monospace' }}>
            {edition.validation_log}
          </div>
        )}
        {!edition.lineup_url && (
          <div style={{ fontSize: 11, color: 'var(--orange)', marginTop: 4 }}>
            ⚠ no `lineup_url` on this edition — site validation can't run
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, fontWeight: 700 }}>
        Full lineup ({lineup.length} acts)
      </div>
      {lineup.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          No lineup linked yet. Populate via lineup OSINT (Phase F.2).
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
          {lineup.map(a => (
            <div key={a.id} style={{ padding: '4px 8px', background: 'var(--card-bg)', borderRadius: 3, fontSize: 12 }}>
              {a.is_sound_system ? '🔊 ' : ''}{a.artist_name}
              {a.stage_name && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.stage_name}</div>}
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 12, padding: 8, background: 'rgba(0,0,0,0.3)', borderRadius: 4, fontSize: 11, color: 'var(--text-muted)' }}>
        <div><strong>slug:</strong> {edition.slug}</div>
        <div><strong>kind (seed):</strong> {edition.kind}{edition.dub_stage_name ? ` (stage: ${edition.dub_stage_name})` : ''}</div>
        <div><strong>status:</strong> {edition.status}{edition.verified ? ' · ✓ verified' : ' · seed only'}</div>
        {edition.notes && <div><strong>notes:</strong> {edition.notes}</div>}
      </div>
    </div>
  );
}

function CandidatesPanel({ candidates, onDismiss, onReject }: {
  candidates: FestivalCandidate[];
  onDismiss: (id: string) => void;
  onReject: (id: string) => void;
}) {
  if (candidates.length === 0) {
    return (
      <div className="empty-state">
        <p>No pending candidates. Run the harvest to scan the last 90 days of scraped events for cross-handle signals.</p>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
        Handles surfaced via cross-mention. Tier 1 = surfaced by a specialist / admin-locked source (single mention enough).
        Tier 0 = needs ≥ 2 distinct sources.
      </div>
      {candidates.map(c => {
        const mentioning = (() => { try { return JSON.parse(c.mentioning_source_ids || '[]'); } catch { return []; } })();
        return (
          <div key={c.id} style={{
            padding: 10, marginBottom: 8, border: '1px solid var(--border-color)', borderRadius: 6,
            background: c.trust_tier_max >= 1 ? 'rgba(201,168,76,0.08)' : 'var(--card-bg)',
            display: 'flex', gap: 12, alignItems: 'center',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                @{c.handle}
                {!!c._looks_festival && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#4caf50', color: '#000', fontWeight: 700 }}>FESTIVAL-LIKE</span>}
                {c.trust_tier_max >= 1 && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'var(--gold)', color: '#000', fontWeight: 700 }}>TIER 1</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {c.mention_count}× mentions · {mentioning.length} distinct source{mentioning.length !== 1 ? 's' : ''}
                {c.has_reshare_url ? ' · 🔗 reshare-url' : ''}
                {c.has_caption_mention ? ' · @ mention' : ''}
                {!!c.surfaced_via_trusted && ' · 🎤 via trusted artist'}
              </div>
              {c.external_website && (
                <div style={{ fontSize: 11, marginTop: 3 }}>
                  <a href={c.external_website} target="_blank" rel="noopener noreferrer"
                     onClick={e => e.stopPropagation()}
                     style={{ color: 'var(--green)', textDecoration: 'none', fontWeight: 600 }}>
                    🌐 {(() => { try { return new URL(c.external_website!).hostname; } catch { return c.external_website; } })()}
                  </a>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 10 }}>dedicated website → likely real festival</span>
                </div>
              )}
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                first seen {new Date(c.first_seen_at).toLocaleDateString()} · last {new Date(c.last_seen_at).toLocaleDateString()}
              </div>
            </div>
            <a href={`https://www.instagram.com/${c.handle}/`} target="_blank" rel="noopener noreferrer"
               className="btn btn-sm" style={{ textDecoration: 'none' }}>Open IG</a>
            <button className="btn btn-sm" onClick={() => onDismiss(c.id)}>Dismiss</button>
            <button className="btn btn-sm btn-danger" onClick={() => onReject(c.id)}>Not festival</button>
          </div>
        );
      })}
    </div>
  );
}
